import type { SvgIconComponent } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import MicIcon from '@mui/icons-material/Mic';
import MovieEditIcon from '@mui/icons-material/MovieEdit';
import SearchIcon from '@mui/icons-material/Search';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { ALL_PROJECT_STATES, PROJECT_STAGES, STAGE_SUBSTATUSES, isReadyState, type ProjectStage } from '@stages/stages';
import {
  getRowBackgroundByBase,
  getStatusBadgeColorByBase,
} from '@/styles/projectColorTokens';

/** Фиксированные статусы проектов — сверху вниз: Готов → Добавлен. Цепочка этапов — общий модуль с сервером */
export const PROJECT_STATUSES: readonly ProjectStage[] = [...PROJECT_STAGES].reverse();

export type ProjectStatus = ProjectStage;

/** Подстатусы для статусов с детализацией. Ключ — базовый статус, значение — массив подстатусов */
export const SUBSTATUSES: Record<string, readonly string[]> = Object.fromEntries(
  PROJECT_STAGES.filter((stage) => STAGE_SUBSTATUSES[stage].length > 0).map((stage) => [stage, STAGE_SUBSTATUSES[stage]]),
);

/** Статусы, у которых есть подменю (показываем >) */
export const STATUSES_WITH_SUBMENU: readonly ProjectStage[] = PROJECT_STAGES.filter(
  (stage) => STAGE_SUBSTATUSES[stage].length > 0,
);

/** Завершённые подстатусы — в таблице: чёрный текст, жирное начертание (см. projects-table__state-btn_complete) */
export const COMPLETED_SUBSTATUSES = [
  'Запись: Завершена',
  'Монтаж: Завершён',
  'Проверка: Завершена',
  'Правки: Внесены',
  'Проверка правок: Завершена',
] as const;

export function isCompletedStatus(state: string | null | undefined): boolean {
  if (!state) return false;
  return (COMPLETED_SUBSTATUSES as readonly string[]).includes(state);
}

/** Получить полное значение статуса: "Статус: Подстатус" */
export function getFullStatus(baseStatus: string, substatus: string): string {
  return `${baseStatus}: ${substatus}`;
}

/** Извлечь базовый статус из полного значения ("Монтаж: В работе" → "Монтаж") */
export function getBaseStatus(state: string | null | undefined): string {
  if (!state) return 'Добавлен';
  const colonIdx = state.indexOf(': ');
  return colonIdx >= 0 ? state.slice(0, colonIdx) : state;
}

/** Извлечь подстатус из полного значения ("Монтаж: В работе" → "В работе"). null если подстатуса нет */
export function getSubstatus(state: string | null | undefined): string | null {
  if (!state) return null;
  const colonIdx = state.indexOf(': ');
  return colonIdx >= 0 ? state.slice(colonIdx + 2) : null;
}

/**
 * Проект не входит в выборку GET /projects?excludeReady=1
 * (сервер: SQL_READY_BRANCH — те же три значения «готовности»).
 * Учитываются: «Готов», «Готов: Частично оплачен», «Готов: Полностью оплачен».
 */
export function isProjectExcludedFromActiveTable(state: string | null | undefined): boolean {
  return isReadyState(state);
}

/** Все допустимые значения state (базовые + с подстатусами) */
export function getAllValidStates(): string[] {
  return [...ALL_PROJECT_STATES];
}

/** Иконки MUI для статусов */
export const STATUS_ICONS: Record<ProjectStatus, SvgIconComponent> = {
  Добавлен: AddIcon,
  Запись: MicIcon,
  Монтаж: MovieEditIcon,
  Проверка: SearchIcon,
  Правки: EditNoteIcon,
  'Проверка правок': SearchIcon,
  Готов: TaskAltIcon,
};

/** Цвета для бейджа статуса: фон = цвет строки, текст чуть темнее */
export function getStatusBadgeColor(state: string | null | undefined): { bg: string; color: string } {
  return getStatusBadgeColorByBase(getBaseStatus(state));
}

/** Цвет фона строки таблицы по статусу */
export function getRowBackground(state: string | null | undefined): string {
  return getRowBackgroundByBase(getBaseStatus(state));
}
